import { useEffect, useState } from 'react';
import type { AnalyticsSchemaDraftDocument } from '@voogzy/contracts';
import { readError, request } from '../../app/api';
import { formatDateTime } from '../../shared/date-time';
import { SchemaCategoriesEditor } from './SchemaCategoriesEditor';
import { SchemaEnumsEditor } from './SchemaEnumsEditor';
import { SchemaEventsEditor } from './SchemaEventsEditor';
import { SchemaReleases } from './SchemaReleases';
import { SchemaReview } from './SchemaReview';
import type { SchemaEditorCommonProps, SchemaEditorTab, SchemaRelease, SchemaState } from './schema-editor-types';

const tabs: Array<{ id: SchemaEditorTab; label: string }> = [
  { id: 'categories', label: 'Categories' },
  { id: 'enums', label: 'Enums' },
  { id: 'events', label: 'Events' },
  { id: 'review', label: 'Review & publish' },
  { id: 'releases', label: 'Releases' },
];

export function SchemaView(props: { environmentId: string; csrfToken: string; canManage: boolean }): React.JSX.Element {
  const [state, setState] = useState<SchemaState | null>(null);
  const [document, setDocument] = useState<AnalyticsSchemaDraftDocument | null>(null);
  const [savedDocumentJson, setSavedDocumentJson] = useState('');
  const [activeTab, setActiveTab] = useState<SchemaEditorTab>('events');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(loadEnvironment, [props.environmentId]);
  useEffect(registerUnloadGuard, [document, savedDocumentJson]);

  if (state === null || document === null) {
    return <div className="surface schema-loading">Loading the schema workspace…</div>;
  }

  const dirty = JSON.stringify(document) !== savedDocumentJson;
  const common: SchemaEditorCommonProps = {
    document,
    canManage: props.canManage,
    busy,
    dirty,
    onDocumentChange: changeDocument,
    onSave: saveDraft,
    onReset: resetDraft,
  };
  const changedEvents = countChangedEvents(state);
  return (
    <section className="schema-layout">
      {error !== '' && <div className="error-banner schema-message">{error}</div>}
      {notice !== '' && <div className="success-banner schema-message">{notice}</div>}
      <section className="schema-summary-grid">
        <SchemaMetric label="Categories" value={document.categories.length} />
        <SchemaMetric label="Enums" value={document.enums.length} />
        <SchemaMetric label="Events" value={document.events.length} />
        <SchemaMetric label="Contract changes" value={changedEvents} accent={changedEvents > 0} />
      </section>

      <article className="surface schema-workspace-header">
        <div>
          <span className="eyebrow">Shared environment draft</span>
          <h2>Schema workspace</h2>
          <p>{props.canManage ? 'Edit definitions independently, save incomplete work, then validate and publish one immutable schema release.' : 'You can inspect the shared draft and release history. Schema management permission is required to edit or publish.'}</p>
        </div>
        <div className="schema-draft-status">
          <span className={dirty ? 'dirty-indicator' : 'clean-indicator'}>{dirty ? 'Unsaved changes' : `Draft r${state.draftRevision} saved`}</span>
          <small>{state.updatedBy === null ? 'Initialized from release history' : `Updated by ${state.updatedBy}`} · {formatDate(state.updatedAt)}</small>
        </div>
      </article>

      <nav className="schema-tabs" aria-label="Schema editors">{renderTabs(dirty)}</nav>
      {renderActiveTab(common)}
    </section>
  );

  function loadEnvironment(): void {
    void loadState();
  }

  function registerUnloadGuard(): () => void {
    function warnBeforeUnload(event: BeforeUnloadEvent): void {
      if (document !== null && JSON.stringify(document) !== savedDocumentJson) {
        event.preventDefault();
      }
    }
    window.addEventListener('beforeunload', warnBeforeUnload);
    return function unregisterUnloadGuard(): void {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }

  function renderTabs(dirty: boolean): React.ReactNode {
    const buttons: React.JSX.Element[] = [];
    for (const tab of tabs) {
      const issueCount = countIssuesForTab(state!, tab.id);
      buttons.push(
        <button className={activeTab === tab.id ? 'active' : ''} data-tab={tab.id} key={tab.id} onClick={selectTab} type="button">
          {tab.label}
          {issueCount > 0 && <span>{issueCount}</span>}
          {dirty && tab.id === 'review' && <i />}
        </button>,
      );
    }
    return buttons;
  }

  function renderActiveTab(common: SchemaEditorCommonProps): React.ReactNode {
    if (activeTab === 'categories') {
      return <SchemaCategoriesEditor {...common} />;
    }
    if (activeTab === 'enums') {
      return <SchemaEnumsEditor {...common} />;
    }
    if (activeTab === 'events') {
      return <SchemaEventsEditor {...common} eventVersions={state!.eventVersions} />;
    }
    if (activeTab === 'review') {
      return <SchemaReview state={state!} busy={busy} dirty={common.dirty} canManage={props.canManage} onSave={saveDraft} onReset={resetDraft} onPublish={publishSchema} />;
    }
    return <SchemaReleases state={state!} busy={busy} dirty={common.dirty} canManage={props.canManage} onLoadRelease={loadRelease} onRestoreRelease={restoreRelease} />;
  }

  function selectTab(event: React.MouseEvent<HTMLButtonElement>): void {
    setActiveTab(event.currentTarget.dataset.tab as SchemaEditorTab);
    setError('');
    setNotice('');
  }

  function changeDocument(nextDocument: AnalyticsSchemaDraftDocument): void {
    setDocument(nextDocument);
    setNotice('');
  }

  function resetDraft(): void {
    if (state !== null) {
      setDocument(structuredClone(state.document));
      setError('');
      setNotice('Unsaved changes were reset.');
    }
  }

  async function loadState(): Promise<void> {
    if (props.environmentId === '') {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await request<SchemaState>(schemaPath(''));
      applyState(response);
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  function applyState(response: SchemaState): void {
    const json = JSON.stringify(response.document);
    setState(response);
    setDocument(structuredClone(response.document));
    setSavedDocumentJson(json);
  }

  async function saveDraft(): Promise<void> {
    if (state === null || document === null) {
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await request<SchemaState>(schemaPath('/draft'), {
        method: 'PUT',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ draftRevision: state.draftRevision, document }),
      });
      applyState(response);
      setNotice(response.validationIssues.length === 0
        ? 'Draft saved. It is ready for review and publication.'
        : `Draft saved with ${response.validationIssues.length} validation issue${response.validationIssues.length === 1 ? '' : 's'}.`);
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function publishSchema(): Promise<void> {
    if (state === null) {
      return;
    }
    if (!window.confirm('Publish the saved draft as the active immutable schema release for this environment?')) {
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await request<{ created: boolean; release: SchemaRelease }>(schemaPath('/publish'), {
        method: 'POST',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ draftRevision: state.draftRevision }),
      });
      await loadState();
      setNotice(response.created
        ? `Published and activated schema revision ${response.release.revision}.`
        : `The schema already matches immutable revision ${response.release.revision}; registry bindings were refreshed.`);
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function loadRelease(revision: number): Promise<SchemaRelease> {
    setError('');
    try {
      return await request<SchemaRelease>(schemaPath(`/releases/${revision}`));
    } catch (caught) {
      setError(readError(caught));
      throw caught;
    }
  }

  async function restoreRelease(revision: number): Promise<void> {
    if (state === null) {
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await request<SchemaState>(schemaPath(`/releases/${revision}/restore`), {
        method: 'POST',
        headers: { 'X-CSRF-Token': props.csrfToken },
        body: JSON.stringify({ draftRevision: state.draftRevision }),
      });
      applyState(response);
      setActiveTab('review');
      setNotice(`Revision ${revision} is now the shared draft. Review it before publishing.`);
    } catch (caught) {
      setError(readError(caught));
    } finally {
      setBusy(false);
    }
  }

  function schemaPath(suffix: string): string {
    return `/admin/v1/reports/schema${suffix}?environmentId=${encodeURIComponent(props.environmentId)}`;
  }
}

function SchemaMetric(props: { label: string; value: number; accent?: boolean }): React.JSX.Element {
  return <article className={props.accent === true ? 'schema-metric accent' : 'schema-metric'}><span>{props.label}</span><strong>{props.value}</strong></article>;
}

function countChangedEvents(state: SchemaState): number {
  let count = 0;
  for (const event of state.eventVersions) {
    if (event.changed) {
      count += 1;
    }
  }
  return count;
}

function countIssuesForTab(state: SchemaState, tab: SchemaEditorTab): number {
  if (tab === 'review') {
    return state.validationIssues.length;
  }
  let count = 0;
  for (const issue of state.validationIssues) {
    if (issue.pointer.startsWith(`${tab}.`)) {
      count += 1;
    }
  }
  return count;
}

function formatDate(value: string): string { return formatDateTime(value); }
